'use client';

import React, { useLayoutEffect, useRef, useState } from 'react';
import Image from 'next/image';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CTAButton from '../ui/CTAButton';
import { trackEvent } from '@/lib/analytics';
import { useInView } from '@/lib/useInView';

gsap.registerPlugin(ScrollTrigger);

// Hard-coded career images
const CAREER_IMAGES = [
  '/images/careers/careers-1.png',
  '/images/careers/careers-2.png',
  '/images/careers/careers-3.png',
  '/images/careers/careers-4.png',
  '/images/careers/careers-5.png',
  '/images/careers/careers-6.png',
  '/images/careers/careers-7.png',
];

// Image position presets for scattered layout - fixed positions matching design
const IMAGE_POSITIONS = [
  { 
    // DIEGO, JAD, JESS AT DESK
    mobileLeft: '10%', mobileTop: '0%', width: '11.25rem', height: '13.4375rem',
    left: '60%', top: '-2%', mdWidth: '32.125rem', mdHeight: '29.125rem'
  },
  { 
    // OLIVIA
    mobileLeft: '45%', mobileTop: '1%', width: '12.8125rem', height: '14.75rem',
    left: '20%', top: '5%', mdWidth: '20.25rem', mdHeight: '23.125rem'
  },
  { 
    // DOG
    mobileLeft: '5%', mobileTop: '12%', width: '12.8125rem', height: '11.3125rem',
    left: '5%', top: '20%', mdWidth: '18.75rem', mdHeight: '17.5rem'
  },
  { 
    // KEVIN, NATHAN, ESTHER, ANGUS
    mobileLeft: '10%', mobileTop: '58%', width: '15rem', height: '10rem',
    left: '48%', top: '40%', mdWidth: '29.5rem', mdHeight: '18.1875rem'
  },
  { 
    // JAMES, GAUIS, KATE
    mobileLeft: '8%', mobileTop: '30%', width: '21.4375rem', height: '17.9375rem',
    left: '8%', top: '43%', mdWidth: '27.5625rem', mdHeight: '23.0625rem'
  },
  { 
    // WALKING TO STAIRS
    mobileLeft: '45%', mobileTop: '70%', width: '11.75rem', height: '11.75rem',
    left: '68%', top: '58%', mdWidth: '22.5rem', mdHeight: '17.5rem'
  },
  { 
    // RACHEL, ANGUS, DIEGO ON COUCH
    mobileLeft: '10%', mobileTop: '89%', width: '21.4375rem', height: '14.3125rem',
    left: '25%', top: '75%', mdWidth: '35.25rem', mdHeight: '23.625rem'
  },
];

interface ImagesWavyBgProps {
  ctaLink: string;
  pathname: string;
}

/**
 * ImagesWavyBg - Join our team section with parallax images and wavy background
 * 
 * Features:
 * - "Join our team" CTA section with animated underline
 * - Parallax scrolling images with force field mouse interaction
 * - Wavy background pattern
 */
export default function ImagesWavyBg({ ctaLink, pathname }: ImagesWavyBgProps) {
  const ENABLE_FORCE_FIELD = false; // Set to true to re-enable mouse rollover movement effect
  
  const containerRef = useRef<HTMLDivElement>(null);
  const imagesRef = useRef<(HTMLDivElement | null)[]>([]);
  const mattersRef = useRef<HTMLSpanElement>(null);
  const mattersUnderlineRef = useRef<SVGPathElement>(null);
  
  // Refs for animate-in-view
  const joinEyebrowRef = useInView<HTMLHeadingElement>();
  const joinTitleRef = useInView<HTMLHeadingElement>();
  const mobileImageRef = useInView<HTMLDivElement>();
  const bodyTextRef = useInView<HTMLParagraphElement>();
  const exploreJobsButtonRef = useInView<HTMLDivElement>();
  
  // Refs for animate-in-view on each image
  const image1InView = useInView<HTMLDivElement>();
  const image2InView = useInView<HTMLDivElement>();
  const image3InView = useInView<HTMLDivElement>();
  const image4InView = useInView<HTMLDivElement>();
  const image5InView = useInView<HTMLDivElement>();
  const image6InView = useInView<HTMLDivElement>();
  const image7InView = useInView<HTMLDivElement>();
  
  const imageInViewRefs = [image1InView, image2InView, image3InView, image4InView, image5InView, image6InView, image7InView];
  
  // State for mouse position for force field effect
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Handle mouse move for force field effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ENABLE_FORCE_FIELD || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  // Calculate force field offset for each image
  const calculateForceFieldOffset = (imageIndex: number): { x: number; y: number } => {
    if (!ENABLE_FORCE_FIELD) return { x: 0, y: 0 };
    if (!containerRef.current || !imagesRef.current[imageIndex]) return { x: 0, y: 0 };
    
    const imageElement = imagesRef.current[imageIndex];
    if (!imageElement) return { x: 0, y: 0 };
    
    const imageRect = imageElement.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Image center relative to container
    const imageCenterX = imageRect.left - containerRect.left + imageRect.width / 2;
    const imageCenterY = imageRect.top - containerRect.top + imageRect.height / 2;
    
    // Distance from mouse to image center
    const deltaX = mousePosition.x - imageCenterX;
    const deltaY = mousePosition.y - imageCenterY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Force field radius (in pixels)
    const forceRadius = 250;
    
    // If mouse is within force field radius, push the image away
    if (distance < forceRadius && distance > 0) {
      const force = (forceRadius - distance) / forceRadius;
      const maxOffset = 12; // Maximum pixels to shift
      
      return {
        x: -(deltaX / distance) * force * maxOffset,
        y: -(deltaY / distance) * force * maxOffset
      };
    }
    
    return { x: 0, y: 0 };
  };

  // Parallax scroll animation for images (desktop only)
  useLayoutEffect(() => {
    if (!CAREER_IMAGES.length) return;

    const context = gsap.context(() => {
      const mm = gsap.matchMedia();

      // Desktop only - parallax effect
      mm.add('(min-width: 768px)', () => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });

        imagesRef.current.forEach((imageRef, index) => {
          if (imageRef) {
            tl.to(imageRef, {
              y: -80 - (index * 20),
              ease: "none"
            }, index * 0.1);
          }
        });
      });

      return () => mm.revert();
    }, containerRef);

    return () => context.revert();
  }, []);

  // Animate underline when "matters" heading scrolls into view
  useLayoutEffect(() => {
    if (!mattersUnderlineRef.current) return;

    const path = mattersUnderlineRef.current;
    const pathLength = path.getTotalLength();
    
    // Set up the initial state
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: mattersRef.current,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      });
    });

    return () => context.revert();
  }, []);

  return (
    <>
      {/* Custom styles for responsive image sizing */}
      <style jsx>{`
        .wavy-bg-container {
          overflow-x: clip;
          overflow-y: visible;
        }

        .parallax-image {
          left: var(--mobile-left) !important;
          top: var(--mobile-top) !important;
        }
        
        @media (min-width: 768px) {
          .parallax-image {
            width: var(--md-width) !important;
            height: var(--md-height) !important;
            left: var(--md-left) !important;
            top: var(--md-top) !important;
          }
        }
      `}</style>

      {/* Join our team content */}
      <div className="global-padding" id="join-our-team">
        <h3 ref={joinEyebrowRef.ref} className="join-eyebrow heading-2 lg:mb-[0.75rem] mb-[0.75rem] animate-in-view">Join our team</h3>
        <h2 ref={joinTitleRef.ref} className="join-title body-xl-display mb-[1.5rem] animate-in-view">
          Your craft <span ref={mattersRef} className="relative inline-block">
            matters
            <svg 
              className="absolute -bottom-2 left-0 w-full h-auto max-w-[13.25rem]" 
              width="212" 
              height="151" 
              viewBox="0 0 212 20" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path 
                ref={mattersUnderlineRef}
                d="M1.25104 76.3219C2.85456 75.9422 4.45809 75.5625 37.0951 74.7964C69.7321 74.0304 133.354 72.8894 167.295 72.53C203.569 72.6272 208.116 74.0026 210.081 74.5679" 
                stroke="#E60000" 
                strokeWidth="2.5" 
                strokeLinecap="round"
              />
            </svg>
          </span> here.
        </h2>
        {/* Mobile only image */}
        <div ref={mobileImageRef.ref} className="md:hidden mb-[1.5rem] lg:mb-[2rem] animate-in-view">
          <Image src="/images/about-carousel/about-carousel-desktop-07.jpg" alt="Join our team" width={1000} height={1000} className="w-full h-auto rounded-3xl" />
        </div>
        <p ref={bodyTextRef.ref} className="body-default mb-[1.625rem] lg:mb-[1.875rem] animate-in-view">
        Collaboration is alive and well at Havas CX. We champion ideas, challenge perspectives, and build things people actually use. Across strategy, design, storytelling, data, and content, together we can create impactful work that shapes the future of experience. 
        </p>
        {/* CTA Section */}
        <div ref={exploreJobsButtonRef.ref} className="text-left mb-[3.5rem] lg:mb-[5rem] animate-in-view">
          <CTAButton 
            href={ctaLink} 
            showArrow
            onClick={() => {
              trackEvent('careers_click', {
                click_text: 'Explore jobs at Havas',
                click_url: ctaLink || '',
                page_path: pathname
              });
            }}
          >
            Explore jobs at Havas
          </CTAButton>
        </div>
      </div>
      
      {/* Body Images with wavy background */}
      <div className="w-full relative pb-32 wavy-bg-container">
        <div 
          className="absolute top-0 left-0 right-0 bottom-8 bg-repeat bg-top opacity-50 wavy-bg"
          style={{ 
            backgroundImage: 'url(/images/wavy-bg.png)'
          }}
        />
        <div className="relative z-10 w-full flex justify-center">
          <div 
            ref={containerRef} 
            onMouseMove={ENABLE_FORCE_FIELD ? handleMouseMove : undefined}
            className="relative min-h-[68.75rem] md:min-h-[75rem] w-full max-w-8xl px-4"
          >
            {CAREER_IMAGES.map((image, index) => {
              const position = IMAGE_POSITIONS[index];
              const forceOffset = calculateForceFieldOffset(index);
              const inViewRef = imageInViewRefs[index];
              
              return (
                <div 
                  key={index} 
                  ref={el => { 
                    imagesRef.current[index] = el;
                    if (inViewRef?.ref) {
                      (inViewRef.ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
                    }
                  }}
                  className={`absolute parallax-image transition-transform duration-500 ease-out animate-in-view ${index === 0 ? 'hidden md:block' : ''}`}
                  style={{
                    width: position.width,
                    height: position.height,
                    transform: `translate(${forceOffset.x}px, ${forceOffset.y}px)`,
                    '--mobile-left': position.mobileLeft,
                    '--mobile-top': position.mobileTop,
                    '--md-width': position.mdWidth,
                    '--md-height': position.mdHeight,
                    '--md-left': position.left,
                    '--md-top': position.top,
                  } as React.CSSProperties & { 
                    '--mobile-left': string; 
                    '--mobile-top': string;
                    '--md-width': string; 
                    '--md-height': string;
                    '--md-left': string;
                    '--md-top': string;
                  }}
                >
                  <Image 
                    src={image} 
                    alt={`Career life at Havas - Image ${index + 1}`}
                    fill
                    className="object-cover rounded-lg"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

